import { classValidatorResolver } from "@hookform/resolvers/class-validator";
import { camelCase, capitalCase } from "change-case";
import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { View } from "react-native";
import * as ValidatorJS from "validator";

import Input from "@/components/input/Input";
import InputChoose from "@/components/input/InputChoose";
import InputChooseList from "@/components/input/InputChooseList";
import InputDate from "@/components/input/InputDate";
import { BridgeRequirements } from "@/screens/home/userpanel/UserPanel";
import { EnumsOnboarding } from "@/utils/EnumsOnboarding";
import { BridgeUpdateRequest } from "@/utils/dto/application.request";
import { getCountryCode } from "@/utils/onboarding/OnboardingUtils";

interface Props {
  fields: string[];
  fieldValue: (field: string, value: any) => void;
  individualId?: string;
  requirements: BridgeRequirements;
}

type InputComponent = typeof Input | typeof InputDate | typeof InputChooseList;

export default function RenderFields(props: Props) {
  const [multipleSelectionData, setMultipleSelectionData] = useState<
    { value: string; code: string; uniqueKey: string }[]
  >([]);
  const initialZIndex = 200;
  const multipleSelectionFields = ["engageInActivities"];

  useEffect(() => {
    Object.keys(multipleSelectionData).forEach((key) => {
      const codes = multipleSelectionData[key].map((item) => item.code);
      props.fieldValue(key, codes);
    });
  }, [multipleSelectionData]);

  const resolver = classValidatorResolver(BridgeUpdateRequest, {
    validator: { skipMissingProperties: true },
  });

  const { control, setError } = useForm<any>({
    mode: "onBlur",
    resolver,
  });

  const validatePostalCode = (fieldNameToSet: string, value: string) => {
    const countryCode = getCountryCode(props.requirements);
    if (ValidatorJS.isPostalCode(value, countryCode)) {
      props.fieldValue(fieldNameToSet, value);
    } else {
      setError(fieldNameToSet, {
        type: "onBlur",
        message: "Postal code is not valid to specified country",
      });
      props.fieldValue(fieldNameToSet, value);
    }
  };

  const handleSelection = (
    data: { value: string; code: string },
    name: string
  ) => {
    setMultipleSelectionData((prevState) => {
      const isValueAlreadyPresent = prevState[name]?.some(
        (item) => item.value === data.value
      );

      if (isValueAlreadyPresent) {
        return prevState;
      }
      return {
        ...prevState,
        [name]: [
          ...(prevState[name] || []),
          { value: data.value, code: data.code },
        ],
      };
    });
  };

  const handleDeleteItem = (key: string, value: string, code: string) => {
    setMultipleSelectionData((prevState) => ({
      ...prevState,
      [key]: prevState[key]?.filter((item) => item.value !== value),
    }));
  };

  const checkFieldType = (item: any, index: number) => {
    const field = item.startsWith("INDIVIDUAL")
      ? camelCase(item.split("_")[3])
      : camelCase(item);
    const fieldNameToSet = item.startsWith("INDIVIDUAL")
      ? `${item.split("_")[2]}_${field}`
      : field;
    const zIndex = initialZIndex - index * 10;

    let InputComponent: InputComponent;
    if (EnumsOnboarding[field]) {
      InputComponent = InputChooseList;
    } else if (field === "relationshipEstablishedAt") {
      InputComponent = InputDate;
    } else {
      InputComponent = Input;
    }

    return (
      <View
        key={index}
        className={`${zIndex <= 50 ? `z-${zIndex}` : `z-[${zIndex}]`}`}
      >
        <InputComponent
          control={control}
          name={fieldNameToSet}
          label={`${capitalCase(field)} ${
            item.startsWith("INDIVIDUAL") ? ` - (${item.split("_")[1]})` : ""
          }`}
          {...(InputComponent === InputChooseList && {
            data: {
              [EnumsOnboarding[field][0]]: EnumsOnboarding[field][1],
            },
          })}
          {...(multipleSelectionFields.includes(field) && {
            isMultipleSelection: true,
            deleteItem: (value, code) => handleDeleteItem(field, value, code),
            handleSelectedItem: (data: { value: string; code: string }) => {
              handleSelection(data, field);
            },
            selectionArray: multipleSelectionData[field] || [],
          })}
          {...(!multipleSelectionFields.includes(field)
            ? {
                handleChange: (newValue: string) => {
                  if (field === "postalCode") {
                    validatePostalCode(fieldNameToSet, newValue);
                  } else {
                    props.fieldValue(fieldNameToSet, newValue);
                  }
                },
              }
            : {})}
        />
      </View>
    );
  };
  return props.fields.map((item: any, index: number) =>
    checkFieldType(item, index)
  );
}
